import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApi } from '../../hooks/useApi'
import CaptchaWidget from '../../components/CaptchaWidget'
import { TelegramIcon, GamepadIcon, GiftCardIcon } from '../../components/BrandIcons'
import { ArrowLeft, Loader2, AlertTriangle, ShieldCheck, CheckCircle, XCircle, Package, Pencil, UserCircle } from 'lucide-react'

const categoryConfig = {
  telegram: { label: 'Telegram', inputLabel: 'Telegram Username', placeholder: '@username', icon: TelegramIcon, img: null, color: '#2AABEE', gradient: 'from-[#2AABEE] to-[#229ED9]' },
  pubgm: { label: 'PUBG Mobile', inputLabel: 'Player ID', placeholder: 'Enter your Player ID (numbers only)', icon: GamepadIcon, img: 'https://api.g2bulk.com/images/pubgm.png', color: '#F2A900', gradient: 'from-[#F2A900] to-[#E8960A]' },
  gaming: { label: 'Gaming', inputLabel: 'Player ID', placeholder: 'Enter your Player ID', icon: GamepadIcon, img: null, color: '#8b5cf6', gradient: 'from-violet-500 to-purple-600' },
  giftcard: { label: 'Gift Cards', inputLabel: 'Email', placeholder: 'Enter your email', icon: GiftCardIcon, img: null, color: '#059669', gradient: 'from-emerald-500 to-teal-600' },
}

const RedeemFlow = () => {
  const { category } = useParams()
  const navigate = useNavigate()
  const api = useApi()
  const isValidCategory = !!categoryConfig[category]
  const config = categoryConfig[category] || categoryConfig.telegram // fallback for hooks stability
  const BrandIcon = config.icon

  // Load saved username from localStorage
  const storageKey = `g2bulk_user_${category || 'default'}`
  const savedData = (() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return typeof parsed === 'object' ? parsed : { username: raw, displayName: '', photoUrl: '' }
    } catch { return null }
  })()
  const savedUser = savedData?.username || ''

  const [step, setStep] = useState(savedUser ? 1 : 0)

  // Redirect if invalid category
  useEffect(() => {
    if (!isValidCategory) {
      navigate('/', { replace: true })
    }
  }, [isValidCategory, navigate])

  // If loaded from cache, refresh profile in background
  useEffect(() => {
    if (savedUser && !displayName) {
      fetch('/v1/redeem/validate-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: savedUser, category: category || 'telegram' })
      }).then(r => r.json()).then(data => {
        if (data.valid) {
          setDisplayName(data.display_name || '')
          setPhotoUrl(data.photo_url || '')
          try { localStorage.setItem(storageKey, JSON.stringify({ username: data.username || savedUser, displayName: data.display_name || '', photoUrl: data.photo_url || '' })) } catch {}
        }
      }).catch(() => {})
    }
  }, []) // 0=enter username, 1=enter code, 2=confirm
  const [userInput, setUserInput] = useState(savedUser)
  const [userValidated, setUserValidated] = useState(!!savedUser)
  const [displayName, setDisplayName] = useState(savedData?.displayName || '')
  const [photoUrl, setPhotoUrl] = useState(savedData?.photoUrl || '')
  const [validatingUser, setValidatingUser] = useState(false)
  const [code, setCode] = useState('')
  const [product, setProduct] = useState(null)
  const [captchaToken, setCaptchaToken] = useState(null)
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState(null)
  const [editingUser, setEditingUser] = useState(false)

  const rawCode = code.replace(/[^A-Z0-9]/gi, '').toUpperCase()
  const isCodeComplete = rawCode.length === 15

  const formatCode = (input) => {
    const cleaned = input.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 15)
    let fmt = cleaned
    if (cleaned.length > 3) fmt = cleaned.slice(0, 3) + '-' + cleaned.slice(3)
    if (cleaned.length > 7) fmt = cleaned.slice(0, 3) + '-' + cleaned.slice(3, 7) + '-' + cleaned.slice(7)
    if (cleaned.length > 11) fmt = cleaned.slice(0, 3) + '-' + cleaned.slice(3, 7) + '-' + cleaned.slice(7, 11) + '-' + cleaned.slice(11)
    return fmt
  }

  const maskCode = (c) => c && c.length >= 4 ? `G2B-****-****-${c.slice(-4)}` : c

  const goToStep = (s) => { setError(null); setStep(s) }

  const isTelegram = category === 'telegram'

  // Step 0: Validate username
  const handleValidateUser = async () => {
    const trimmed = isTelegram ? userInput.trim().replace(/^@/, '') : userInput.trim()
    if (!trimmed) { setError(`Please enter your ${config.inputLabel}`); return }
    
    setValidatingUser(true); setError(null)
    try {
      // We need a valid code to call validate-user, so just call Fragment API directly
      const res = await fetch('/v1/redeem/validate-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: trimmed, category: category || 'telegram' })
      })
      const data = await res.json()
      
      if (data.valid) {
        setUserInput(data.username || trimmed)
        setDisplayName(data.display_name || '')
        setPhotoUrl(data.photo_url || '')
        setUserValidated(true)
        try { localStorage.setItem(storageKey, JSON.stringify({ username: data.username || trimmed, displayName: data.display_name || '', photoUrl: data.photo_url || '' })) } catch {}
        goToStep(1)
      } else {
        setError(data.message || `Invalid ${config.inputLabel}. Please check and try again.`)
      }
    } catch (err) {
      // If endpoint doesn't exist yet, do basic validation and proceed
      if (trimmed.length >= 3) {
        setUserInput(trimmed)
        setUserValidated(true)
        try { localStorage.setItem(storageKey, trimmed) } catch {}
        goToStep(1)
      } else {
        setError(`${config.inputLabel} must be at least 3 characters`)
      }
    } finally { setValidatingUser(false) }
  }

  // Step 1: Validate code
  const handleValidateCode = async () => {
    if (!isCodeComplete) { setError('Please enter a complete 15-character code'); return }
    setLoading(true); setError(null)
    try {
      const result = await api.validateCode(rawCode)
      if (result.valid && result.product) {
        if (category && result.product.category !== category) {
          setError('This code is for a different product category.'); return
        }
        setProduct(result.product)
        setShowCaptcha(result.product?.show_captcha || false)
        
        // Re-validate user with the code context
        const userResult = await api.validateUser(rawCode, userInput)
        if (!userResult.valid) {
          setError(userResult.message || `Invalid ${config.inputLabel}`)
          return
        }
        goToStep(2)
      } else {
        setError(result.message || 'Invalid or expired code.')
      }
    } catch (err) {
      setError(err.message || 'Verification failed.')
    } finally { setLoading(false) }
  }

  // Step 2: Confirm
  const handleConfirm = async () => {
    if (showCaptcha && !captchaToken) { setError('Please complete the security check.'); return }
    setConfirming(true); setError(null)
    try {
      const result = await api.confirmRedemption(rawCode, userInput, captchaToken)
      if (result.success) {
        if (result.receipt_token) navigate(`/receipt/${result.receipt_token}`)
        else if (result.redemption_id) navigate(`/status/${result.redemption_id}`)
      } else setError(result.message || 'Redemption failed.')
    } catch (err) { setError(err.message || 'Failed to process.') }
    finally { setConfirming(false) }
  }

  const handleEditUser = () => {
    setEditingUser(true)
    setUserValidated(false)
    setProduct(null)
    setCode('')
    goToStep(0)
  }

  const StepBadge = ({ num, active }) => (
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
      active ? `bg-gradient-to-br ${config.gradient} text-white shadow-sm` : 'bg-gray-200 text-gray-400'
    }`}>{num}</div>
  )

  // Don't render anything for invalid category (redirect will happen via useEffect)
  if (!isValidCategory) return null

  return (
    <div className="container mx-auto px-4 py-6 max-w-md">
      {/* Category header */}
      <div className="flex items-center space-x-3 mb-6">
        <button onClick={() => navigate('/')} className="text-text-muted hover:text-text-primary">
          <ArrowLeft size={20} />
        </button>
        {config.img ? (
          <img src={config.img} alt="" className="w-10 h-10 rounded-xl object-cover shadow-sm" />
        ) : (
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-sm`}>
            <BrandIcon size={22} className="text-white" />
          </div>
        )}
        <div>
          <h1 className="text-lg font-bold text-text-primary">{config.label}</h1>
          <p className="text-text-muted text-xs">Code Redemption</p>
        </div>
      </div>

      {/* Step 1: Username/Player ID (shown first) */}
      <div className={`card mb-4 ${step >= 0 ? '' : 'opacity-40'}`}>
        <div className="flex items-center space-x-2 mb-4">
          <StepBadge num="1" active={step >= 0} />
          <h2 className="font-semibold text-text-primary uppercase text-sm tracking-wide">{config.inputLabel}</h2>
        </div>

        {step === 0 && !userValidated ? (
          <div className="animate-fade-in">
            <input
              type="text"
              value={userInput}
              onChange={(e) => { setUserInput(e.target.value); setError(null) }}
              onKeyDown={(e) => e.key === 'Enter' && handleValidateUser()}
              placeholder={config.placeholder}
              className="input mb-3"
              autoFocus
            />

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 animate-fade-in">
                <p className="text-error text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            <button onClick={handleValidateUser} disabled={validatingUser || !userInput.trim()}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-40 bg-gradient-to-r ${config.gradient} hover:shadow-md`}>
              {validatingUser ? (
                <span className="flex items-center justify-center"><Loader2 size={18} className="animate-spin mr-2" />Verifying...</span>
              ) : 'Continue'}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2.5">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm" />
              ) : (
                <UserCircle size={36} className="text-gray-400 flex-shrink-0" />
              )}
              <div>
                {isTelegram ? (
                  <>
                    {displayName && <p className="text-text-primary font-semibold text-sm leading-tight">{displayName}</p>}
                    <p className={`text-text-muted ${displayName ? 'text-xs' : 'text-sm font-medium text-text-primary'}`}>@{userInput}</p>
                  </>
                ) : (
                  <>
                    {displayName && <p className="text-text-primary font-semibold text-sm leading-tight">{displayName}</p>}
                    <p className={`text-text-muted ${displayName ? 'text-xs' : 'text-sm font-medium text-text-primary'}`}>{config.inputLabel}: {userInput}</p>
                  </>
                )}
              </div>
            </div>
            <button onClick={handleEditUser} className="text-text-muted hover:text-accent p-1" title="Change">
              <Pencil size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Step 2: Enter Code */}
      <div className={`card mb-4 ${step >= 1 ? '' : 'opacity-40 pointer-events-none'}`}>
        <div className="flex items-center space-x-2 mb-4">
          <StepBadge num="2" active={step >= 1} />
          <h2 className="font-semibold text-text-primary uppercase text-sm tracking-wide">Enter Redeem Code</h2>
        </div>

        {step === 1 ? (
          <div className="animate-fade-in">
            <div className="relative mb-3">
              <input
                type="text"
                value={formatCode(code)}
                onChange={(e) => { setCode(e.target.value); setError(null) }}
                onPaste={(e) => { e.preventDefault(); setCode(e.clipboardData.getData('text')); setError(null) }}
                onKeyDown={(e) => e.key === 'Enter' && isCodeComplete && handleValidateCode()}
                placeholder="G2B-XXXX-XXXX-XXXX"
                className="input text-center text-lg h-14 font-mono tracking-widest pr-10"
                maxLength={19}
                autoFocus
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {loading && <Loader2 size={18} className="text-accent animate-spin" />}
                {isCodeComplete && !loading && !error && <CheckCircle size={18} className="text-success" />}
                {error && <XCircle size={18} className="text-error" />}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 animate-fade-in">
                <p className="text-error text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            <button onClick={handleValidateCode} disabled={!isCodeComplete || loading}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-40 bg-gradient-to-r ${config.gradient} hover:shadow-md`}>
              {loading ? <span className="flex items-center justify-center"><Loader2 size={18} className="animate-spin mr-2" />Verifying...</span> : 'Verify Code'}
            </button>
          </div>
        ) : step > 1 ? (
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <div>
              <p className="font-semibold text-text-primary">{product?.name}</p>
              <p className="text-xs text-text-muted font-mono">{maskCode(rawCode)}</p>
            </div>
            <span style={{ color: config.color }} className="text-sm font-bold">{product?.denomination}</span>
          </div>
        ) : null}
      </div>

      {/* Step 3: Confirm */}
      <div className={`card ${step >= 2 ? '' : 'opacity-40 pointer-events-none'}`}>
        <div className="flex items-center space-x-2 mb-4">
          <StepBadge num="3" active={step >= 2} />
          <h2 className="font-semibold text-text-primary uppercase text-sm tracking-wide">Confirm & Redeem</h2>
        </div>

        {step === 2 && (
          <div className="animate-fade-in">
            <div className="bg-gray-50 rounded-lg p-4 divide-y divide-gray-200 mb-4">
              {[
                ['Product', product?.name],
                ['Value', product?.denomination],
                ['Code', maskCode(rawCode)],
                [config.inputLabel, userInput],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between py-2 first:pt-0 last:pb-0">
                  <span className="text-text-muted text-sm">{label}</span>
                  <span className={`text-sm font-medium ${
                    label === 'Value' ? 'font-bold' : label === 'Code' ? 'font-mono text-text-secondary' : 'text-text-primary'
                  }`} style={label === 'Value' ? { color: config.color } : {}}>{val}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <AlertTriangle size={14} className="text-warning mt-0.5 flex-shrink-0" />
              <p className="text-amber-800 text-xs">This action cannot be undone. Please verify the details are correct.</p>
            </div>

            {showCaptcha && (
              <div className="mb-4">
                <CaptchaWidget onVerify={(t) => { setCaptchaToken(t); setError(null) }} onError={() => setCaptchaToken(null)} onExpire={() => setCaptchaToken(null)} />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 animate-fade-in">
                <p className="text-error text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            {confirming ? (
              <div className="text-center py-4">
                <div className="relative w-14 h-14 mx-auto mb-3">
                  <svg className="animate-spin" viewBox="0 0 50 50" style={{ width: 56, height: 56 }}>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="#d1fae5" strokeWidth="4"></circle>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="#059669" strokeWidth="4" strokeDasharray="80" strokeDashoffset="60" strokeLinecap="round"></circle>
                  </svg>
                </div>
                <p className="text-text-primary font-semibold text-sm">Processing your redemption...</p>
                <p className="text-text-muted text-xs mt-1">Please don't close this page</p>
              </div>
            ) : (
              <button onClick={handleConfirm} disabled={showCaptcha && !captchaToken}
                className="w-full py-3.5 rounded-lg font-semibold text-white transition-all disabled:opacity-40 bg-gradient-to-r from-emerald-500 to-green-600 hover:shadow-lg active:scale-[0.99] text-base">
                <span className="flex items-center justify-center"><ShieldCheck size={18} className="mr-2" />Confirm & Redeem</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default RedeemFlow
